import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { Payment, PaymentAllocation, InvoiceStatus, ID } from '@shared/types';
import {
  buildBaseFields,
  buildUpdateFields,
  fromSnapshot,
  writeAuditLog,
} from '@shared/utils';

const paymentCreateSchema = z.object({
  invoiceId: z.string().min(1, '請求書IDは必須です'),
  customerId: z.string().min(1, '顧客IDは必須です'),
  amount: z.number().positive('入金金額は1円以上です'),
  method: z.enum(['bank_transfer', 'credit_card', 'cash', 'other']),
  paidAt: z.instanceof(Timestamp),
  memo: z.string().nullable().default(null),
});

export type PaymentCreateInput = z.infer<typeof paymentCreateSchema>;

const COL = 'payments';
const INV_COL = 'invoices';

export async function fetchPayments(invoiceId: ID): Promise<Payment[]> {
  const q = query(
    collection(db, COL),
    where('invoiceId', '==', invoiceId),
    where('isDeleted', '==', false),
    orderBy('paidAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Payment);
}

export async function fetchPayment(id: ID): Promise<Payment | null> {
  const snap = await getDoc(doc(db, COL, id));
  return fromSnapshot<Payment>(snap);
}

export async function recordPayment(
  data: PaymentCreateInput,
  userId: ID
): Promise<Payment> {
  const validated = parseOrThrow(paymentCreateSchema, data);

  const invRef = doc(db, INV_COL, validated.invoiceId);
  // Transaction 内で新しい payment ドキュメントの ref を事前に生成する
  // （addDoc は Transaction 内で使用不可のため tx.set() を使う）
  const payRef = doc(collection(db, COL));

  const baseFields = buildBaseFields(userId);

  const paymentId = await runTransaction(db, async (tx) => {
    const invSnap = await tx.get(invRef);
    if (!invSnap.exists()) throw new Error('請求書が見つかりません。');
    const inv = invSnap.data();
    if (inv['isLocked']) throw new Error('締め済みの請求書には入金記録を追加できません。');

    const newPaid = (inv['paidAmount'] as number) + validated.amount;
    const newRemaining = (inv['total'] as number) - newPaid;
    const newStatus: InvoiceStatus = newRemaining <= 0 ? 'paid' : 'partially_paid';

    tx.set(payRef, { ...validated, ...baseFields });
    tx.update(invRef, {
      paidAmount: newPaid,
      remainingAmount: Math.max(0, newRemaining),
      status: newStatus,
      ...buildUpdateFields(userId),
    });

    return payRef.id;
  });

  // audit ログは Transaction 外で記録する（失敗しても本体の整合性には影響しない）
  await writeAuditLog({
    entityType: 'payment',
    entityId: paymentId,
    action: 'create',
    userId,
    after: validated,
  });

  return { id: paymentId, ...validated, ...baseFields } as unknown as Payment;
}

// ---------- C4: 多対多消込 ----------

const allocationSchema = z.object({
  invoiceId: z.string().min(1),
  allocatedAmount: z.number().positive(),
});

const multiPaymentInputSchema = z.object({
  customerId: z.string().min(1),
  amount: z.number().positive('入金額は0より大きい値を入力してください'),
  method: z.enum(['bank_transfer', 'credit_card', 'cash', 'other']),
  paidAt: z.instanceof(Timestamp),
  memo: z.string().nullable().default(null),
  allocations: z
    .array(allocationSchema)
    .min(1, '消込対象を1件以上指定してください'),
});

export type MultiPaymentInput = z.infer<typeof multiPaymentInputSchema>;

export async function recordMultiPayment(
  data: MultiPaymentInput,
  userId: ID,
): Promise<Payment> {
  const validated = parseOrThrow(multiPaymentInputSchema, data);

  const totalAlloc = validated.allocations.reduce(
    (s, a) => s + a.allocatedAmount,
    0,
  );
  if (Math.abs(totalAlloc - validated.amount) > 0.01) {
    throw new Error('配分合計が入金額と一致しません。');
  }

  const payment = await runTransaction(db, async (tx) => {
    const invSnaps = await Promise.all(
      validated.allocations.map((a) => tx.get(doc(db, INV_COL, a.invoiceId))),
    );

    for (let i = 0; i < invSnaps.length; i++) {
      const snap = invSnaps[i];
      const alloc = validated.allocations[i];
      if (!snap.exists()) {
        throw new Error(`請求書 ${alloc.invoiceId} が見つかりません。`);
      }
      if (snap.data()['isLocked']) {
        throw new Error(`請求書 ${alloc.invoiceId} は締め済みです。`);
      }
      if (snap.data()['customerId'] !== validated.customerId) {
        throw new Error(`請求書 ${alloc.invoiceId} の顧客が一致しません。`);
      }
      const rawRemaining = snap.data()['remainingAmount'];
      if (typeof rawRemaining !== 'number' || !Number.isFinite(rawRemaining)) {
        throw new Error(`請求書 ${alloc.invoiceId} の残額データが不正です: ${String(rawRemaining)}`);
      }
      const remaining = rawRemaining;
      if (alloc.allocatedAmount > remaining + 0.01) {
        throw new Error(
          `請求書 ${alloc.invoiceId} の残額を超える配分です。`,
        );
      }
    }

    const paymentRef = doc(collection(db, COL));
    const invoiceIds = validated.allocations.map((a) => a.invoiceId);
    const allocations: PaymentAllocation[] = validated.allocations.map((a) => ({
      invoiceId: a.invoiceId,
      allocatedAmount: a.allocatedAmount,
    }));

    tx.set(paymentRef, {
      ...validated,
      invoiceId: invoiceIds[0],
      invoiceIds,
      allocations,
      ...buildBaseFields(userId),
    });

    for (let i = 0; i < invSnaps.length; i++) {
      const snap = invSnaps[i];
      const alloc = validated.allocations[i];
      const invData = snap.data();
      if (!invData) throw new Error('請求書データの取得に失敗しました。');
      const newPaid = (invData['paidAmount'] as number) + alloc.allocatedAmount;
      const grandtotal = (invData['grandtotal'] ?? invData['total']) as number;
      const newRemaining = grandtotal - newPaid;
      const newStatus: InvoiceStatus =
        newRemaining <= 0 ? 'paid' : 'partially_paid';
      tx.update(snap.ref, {
        paidAmount: newPaid,
        remainingAmount: Math.max(0, newRemaining),
        status: newStatus,
        ...buildUpdateFields(userId),
      });
    }

    return {
      id: paymentRef.id,
      ...validated,
      invoiceId: invoiceIds[0],
      invoiceIds,
      allocations,
      ...buildBaseFields(userId),
    } as unknown as Payment;
  });

  await writeAuditLog({
    entityType: 'payment',
    entityId: payment.id,
    action: 'create',
    userId,
    after: {
      amount: validated.amount,
      invoiceCount: validated.allocations.length,
    },
  });

  return payment;
}

export async function fetchPaymentsByPeriod(filters: {
  from: Timestamp;
  to: Timestamp;
  customerId?: ID;
}): Promise<Payment[]> {
  let q = query(
    collection(db, COL),
    where('isDeleted', '==', false),
    where('paidAt', '>=', filters.from),
    where('paidAt', '<=', filters.to),
    orderBy('paidAt', 'desc'),
  );
  if (filters.customerId) {
    q = query(q, where('customerId', '==', filters.customerId));
  }
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Payment);
}

function sanitizeCsvCell(value: string): string {
  // CSVインジェクション防止: =,+,-,@,タブ,CR で始まる値にシングルクォートを前置
  if (/^[=+\-@\t\r]/.test(value)) {
    return `'${value}`;
  }
  return value;
}

export function generatePaymentExcel(payments: Payment[]): string {
  const BOM = '﻿';
  const header = '入金ID,顧客ID,入金日,金額,方法,メモ\r\n';
  const rows = payments
    .map((p) => {
      const date = (p.paidAt as Timestamp)
        .toDate()
        .toLocaleDateString('ja-JP');
      const memo = sanitizeCsvCell((p.memo ?? '').replace(/"/g, '""'));
      const customerId = sanitizeCsvCell(p.customerId);
      const payId = sanitizeCsvCell(p.id);
      return `"${payId}","${customerId}","${date}",${p.amount},"${p.method}","${memo}"`;
    })
    .join('\r\n');
  return BOM + header + rows;
}

// ---------- end C4 ----------

export async function deletePayment(id: ID, userId: ID): Promise<void> {
  const payRef = doc(db, COL, id);

  await runTransaction(db, async (tx) => {
    const paySnap = await tx.get(payRef);
    if (!paySnap.exists()) throw new Error('入金記録が見つかりません。');
    const payment = paySnap.data();
    if (payment['isDeleted'] === true) throw new Error('既に削除済みの入金記録です。');

    const invRef = doc(db, INV_COL, payment['invoiceId'] as string);
    const invSnap = await tx.get(invRef);
    if (!invSnap.exists()) throw new Error('請求書が見つかりません。');
    const inv = invSnap.data();
    if (inv['isLocked']) throw new Error('締め済みの請求書の入金記録は削除できません。');

    // soft delete
    tx.update(payRef, { isDeleted: true, ...buildUpdateFields(userId) });

    // Transaction 内では getDocs が使えないため、
    // 現在の paidAmount からこの入金分を差し引いてアトミックに計算する
    const currentPaid = inv['paidAmount'] as number;
    const thisAmount = payment['amount'] as number;
    const newPaid = Math.max(0, currentPaid - thisAmount);
    const newRemaining = (inv['total'] as number) - newPaid;
    let newStatus: InvoiceStatus;
    if (newPaid <= 0) {
      newStatus = 'issued';
    } else if (newRemaining <= 0) {
      newStatus = 'paid';
    } else {
      newStatus = 'partially_paid';
    }

    tx.update(invRef, {
      paidAmount: newPaid,
      remainingAmount: Math.max(0, newRemaining),
      status: newStatus,
      ...buildUpdateFields(userId),
    });
  });

  // audit ログは Transaction 完了後に記録する
  const paySnap = await getDoc(payRef);
  await writeAuditLog({
    entityType: 'payment',
    entityId: id,
    action: 'delete',
    userId,
    before: paySnap.data(),
  });
}
